<template>
    <div>
        <b-row class="align-items-end mb-2 filter-row">
            <b-col cols="auto">
                <b-form-group label="Search" class="mb-0">
                    <b-form-input v-model="search" debounce="300" placeholder="Name, username, email, NPI or practice" />
                </b-form-group>
            </b-col>
            <b-col cols="auto">
                <b-form-group label="Show" class="mb-0">
                    <b-form-select v-model="status" :options="statusOptions" />
                </b-form-group>
            </b-col>
            <b-col cols="auto">
                <!-- right-aligned for the same reason as the shared ExportDropdown: the menu
                     stays in the layout while closed, so a left-aligned one at the row's right
                     edge overhangs the viewport and leaves the page horizontally scrollable. -->
                <b-dropdown variant="success" text="Export" right toggle-class="m-0" :disabled="!filteredRows.length">
                    <b-dropdown-item @click="saveCSV">
                        <i class="fa fa-fw fa-lg fa-file-csv" />
                        CSV
                    </b-dropdown-item>
                </b-dropdown>
            </b-col>
        </b-row>

        <p v-if="trackingBeganAt" class="text-muted small mb-3">
            Login tracking began {{ formatDate(trackingBeganAt) }}. Logins before that date were not recorded.
        </p>

        <b-alert :show="!!loadError" variant="danger" class="d-flex align-items-center justify-content-between">
            <span>{{ loadError }}</span>
            <b-button size="sm" variant="outline-danger" @click="fetchRows">
                <i class="fa fa-sync"></i> Retry
            </b-button>
        </b-alert>

        <b-table v-if="!loadError" :items="filteredRows" :fields="fields" :busy="loading" show-empty sort-null-last
            :per-page="pageSize" :current-page="pageNumber"
            :empty-text="emptyText" small responsive sort-icon-left>
            <template #table-busy>
                <div class="text-center my-3">
                    <b-spinner small class="align-middle mr-2"></b-spinner>
                    <span>Loading user access…</span>
                </div>
            </template>

            <template #cell(practiceName)="{ item, value }">
                <span :class="{ 'text-muted': !item.practiceName }">{{ value }}</span>
            </template>

            <template #cell(lastLoginAt)="{ item, value }">
                <span :class="{ 'text-muted': !item.lastLoginAt }">{{ value }}</span>
            </template>
        </b-table>

        <BottomPagerBar v-if="!loadError" v-model="pageNumber" item-name="membership" show-total
            show-page-size-selector :total-rows="rows.length" :filtered-rows="filteredRows.length"
            :per-page="pageSize" :loading="loading" @change-page-size="onPageSizeChange($event)">
            <template #left>
                <b-button size="sm" class="m-0" variant="primary" @click="fetchRows">
                    <i class="fa fa-sync"></i> Refresh
                </b-button>
            </template>
        </BottomPagerBar>
    </div>
</template>

<script lang="ts">
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import axios from 'axios';
import moment from 'moment';
import { ReportExporter } from '@/mixins/ReportExporter';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import {
    ProviderWebUserAccessReport,
    ProviderWebUserAccessRow,
    UserAccessStatusFilter
} from '@/models/ProviderWebUserAccess';

const NO_LOGIN = 'No login recorded';
const PAGE_SIZE_KEY = 'pageSize_ProviderWebUserAccess';

@Component({
    name: 'ProviderWebUserAccessReport',
    components: { BottomPagerBar }
})
export default class ProviderWebUserAccessReportComponent extends Mixins(ReportExporter) {
    rows: ProviderWebUserAccessRow[] = [];
    trackingBeganAt: string | null = null;
    loading = false;
    loadError = '';
    loaded = false;
    pageNumber = 1;
    pageSize = 25;
    search = '';
    status: UserAccessStatusFilter = 'current';

    readonly statusOptions = [
        { value: 'current', text: 'Current users' },
        { value: 'noLogin', text: 'Never logged in' },
        { value: 'loggedIn', text: 'Has logged in' },
        { value: 'removed', text: 'Removed users' },
        { value: 'all', text: 'All' }
    ];

    get fields() {
        return [
            { key: 'user', label: 'User', sortable: true },
            { key: 'practiceName', label: 'Practice', sortable: true, formatter: this.formatDash },
            { key: 'email', label: 'Email', sortable: true },
            { key: 'npiNumber', label: 'NPI', sortable: true },
            { key: 'canPrescribe', label: 'Can Prescribe', sortable: true, formatter: this.formatYesNo },
            { key: 'lastLoginAt', label: 'Last Login', sortable: true, formatter: this.formatLastLogin },
            { key: 'loginCount', label: 'Logins (all time)', sortable: true },
            { key: 'isProvisioned', label: 'Status', sortable: true, formatter: this.formatProvisioned },
            { key: 'practiceIsActive', label: 'Practice Active', sortable: true, formatter: this.formatYesNo },
            { key: 'deviceBrowser', label: 'Device / Browser', sortable: true },
            { key: 'timeZone', label: 'Time Zone', sortable: true }
        ];
    }

    get filteredRows(): ProviderWebUserAccessRow[] {
        const term = this.search.trim().toLowerCase();
        return this.rows.filter(row => this.matchesStatus(row) && this.matchesSearch(row, term));
    }

    get emptyText(): string {
        if (!this.loaded) return 'Loading…';
        return this.rows.length
            ? 'No users match the current filters'
            : 'No prescriber portal users found';
    }

    mounted() {
        this.pageSize = getPageSizePreference(this, PAGE_SIZE_KEY, 25);
    }

    // Called by the parent on first tab activation so the query is deferred past page load.
    async ensureLoaded() {
        if (this.loaded) return;
        this.loaded = true;
        await this.fetchRows();
    }

    @Watch('search')
    @Watch('status')
    onFilterChanged() {
        // Reset paging so a narrower filter can't strand the user past the new last page.
        this.pageNumber = 1;
    }

    onPageSizeChange(newSize: number) {
        this.pageSize = newSize;
        this.pageNumber = 1;
        savePageSizePreference(this, PAGE_SIZE_KEY, newSize);
    }

    async fetchRows() {
        try {
            this.loading = true;
            this.loadError = '';
            const resp = await axios.get<ProviderWebUserAccessReport>('/api/ProviderWeb/user-access');
            this.rows = resp.data?.rows ?? [];
            this.trackingBeganAt = resp.data?.trackingBeganAt ?? null;
        } catch (err: any) {
            console.error(err);
            this.rows = [];
            this.trackingBeganAt = null;
            const body = err.response?.data;
            this.loadError = typeof body === 'string' && body.trim()
                ? body
                : 'Failed to load the user access report. The report was not loaded.';
        } finally {
            this.loading = false;
        }
    }

    saveCSV() {
        // Export the whole filtered set, not just the rendered page.
        this.ExportCSV('Prescriber Portal User Access', this.filteredRows, this.fields);
    }

    private matchesStatus(row: ProviderWebUserAccessRow): boolean {
        switch (this.status) {
            case 'current': return row.isProvisioned;
            case 'loggedIn': return row.isProvisioned && !!row.lastLoginAt;
            case 'noLogin': return row.isProvisioned && !row.lastLoginAt;
            case 'removed': return !row.isProvisioned;
            default: return true;
        }
    }

    private matchesSearch(row: ProviderWebUserAccessRow, term: string): boolean {
        if (!term) return true;
        return [row.user, row.username, row.email, row.npiNumber, row.practiceName]
            .some(value => (value || '').toLowerCase().includes(term));
    }

    // ExportCSV coerces a null cell to '' before calling the formatter, hence the string in the union.
    private formatYesNo(value?: boolean | null | string): string {
        if (value === null || value === undefined || value === '') return '—';
        return value ? 'Yes' : 'No';
    }

    private formatDash(value?: string | null): string {
        return value || '—';
    }

    private formatProvisioned(value?: boolean | null): string {
        return value ? 'Current' : 'Removed';
    }

    private formatLastLogin(value?: string | null): string {
        return value ? moment.utc(value).local().format('L LT') : NO_LOGIN;
    }

    formatDate(value?: string | null): string {
        return value ? moment.utc(value).local().format('L') : '';
    }
}
</script>

<style scoped>
.filter-row > [class^='col'] {
    padding-bottom: 0.25rem;
}
</style>
